import { Injectable } from '@angular/core';
import { from, Observable, of, switchMap, tap } from 'rxjs';
import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpParams,
} from '@angular/common/http';
import { environment } from '@src/environments/environment';
import {
  APIRequestBodyModel,
  ContainerModel,
  ContainersResponseBody,
} from '@app/models/server/layout.model';
import { Params, Router } from '@angular/router';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { StorageUtilLibService } from '@b2b/storage-util-lib';

export const BYPASS_LOADER = new HttpContextToken(() => false);

@Injectable({
  providedIn: 'root',
})
export class HttpClientService {
  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private storageUtilLibService: StorageUtilLibService,
  ) {}

  public getDynamicContainersData(
    api: string,
    isCacheEnabled: boolean,
    key: string,
    apiRequestBody: APIRequestBodyModel,
    params: Params,
    skipLoader?: boolean,
  ): Observable<ContainerModel[] | ContainersResponseBody> {
    let urlPath = this.router?.url;
    if (urlPath && urlPath?.includes('?')) {
      urlPath = urlPath.substring(0, urlPath.indexOf('?'));
    }

    let cacheKey: string;

    if (!this.b2bDeviceUtilService.isDesktop()) {
      cacheKey = `${STORAGE_CONST.MOBILE_PAGE_DATA_CACHE_PREFIX_ENUM}${
        STORAGE_CONST.DYNAMIC_CACHE_PREFIX_ENUM
      }${urlPath?.toLowerCase()}_${key}${
        STORAGE_CONST.PAGE_DATA_CACHE_SUFFIX_ENUM
      }`;
    } else {
      cacheKey = `${STORAGE_CONST.DESKTOP_PAGE_DATA_CACHE_PREFIX_ENUM}${
        STORAGE_CONST.DYNAMIC_CACHE_PREFIX_ENUM
      }${urlPath?.toLowerCase()}_${key}${
        STORAGE_CONST.PAGE_DATA_CACHE_SUFFIX_ENUM
      }`;
    }

    if (isCacheEnabled && environment?.mobileApp) {
      return from(this.storageUtilLibService.get(cacheKey, false)).pipe(
        switchMap((res: any) => {
          if (res) {
            return of(res);
          } else {
            return this.getDynamicContainers(
              api,
              apiRequestBody,
              params,
              skipLoader,
            ).pipe(
              tap((containers: ContainerModel[]) => {
                this.storageUtilLibService
                  ?.set(cacheKey, containers, false)
                  .then();
              }),
            );
          }
        }),
      );
    } else {
      return this.getDynamicContainers(api, apiRequestBody, params, skipLoader);
    }
  }

  getExternal(url: string, options: any): Observable<any> {
    return this.httpClient.get(url, options);
  }

  get(url: string, options: any, skipLoader?: boolean): Observable<any> {
    if (skipLoader) {
      options = {
        ...options,
        context: new HttpContext().set(BYPASS_LOADER, true),
      };
    }
    return this.httpClient.get(environment.restAPI + url, options);
  }

  put(url: string, body: any, options: any): Observable<any> {
    return this.httpClient.put(environment.restAPI + url, body, options);
  }

  post(
    url: string,
    body: any,
    options: any,
    skipLoader?: boolean,
  ): Observable<any> {
    if (skipLoader) {
      options = {
        ...options,
        context: new HttpContext().set(BYPASS_LOADER, true),
      };
    }
    return this.httpClient.post(environment.restAPI + url, body, options);
  }

  delete(url: string, options: any): Observable<any> {
    return this.httpClient.delete(environment.restAPI + url, options);
  }

  /*  oldPost(url: string, body: any, options: any): Observable<any> {
      return this.httpClient.post(environment?.oldRestAPI + url, body, options);
    }

    enquiry(body: any): Observable<any> {
      return this.httpClient.post(environment?.enquiryRestAPI, body, {});
    }*/

  private getDynamicContainers(
    api: string,
    apiRequestBody: APIRequestBodyModel,
    params: Params,
    skipLoader: boolean,
  ): Observable<ContainerModel[] | ContainersResponseBody> {
    if (apiRequestBody?.method === 'GET') {
      let httpParams: HttpParams = new HttpParams();
      if (
        apiRequestBody?.queryParams &&
        Object.entries(apiRequestBody?.queryParams)?.length > 0
      ) {
        httpParams = httpParams.appendAll(apiRequestBody?.queryParams);
      }
      return this.get(
        `${api}`,
        {
          params: httpParams,
        },
        skipLoader,
      );
    } else {
      return this.post(
        api,
        {
          queryParams: params,
          apiRequestBody,
        },
        {},
        skipLoader,
      );
    }
  }
}
